import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { RetomadaDatas } from "@/components/RetomadaDatas";

const ROTULOS = {
  reaproveitar_data: "Reaproveitar data alternativa",
  reagendar: "Reagendar",
  falta: "Registrar falta/ausência",
  revisao: "Criar Revisão",
  assistencia: "Criar Assistência Técnica",
  cancelar: "Cancelar OS",
  solicitar_cancelamento: "Solicitar cancelamento",
  ocorrencia_gestao: "Registrar ocorrência",
};
const SOL_ROTULO = { pendente: "Pendente de decisão da Empresa", aprovada: "Aprovada — OS cancelada",
  recusada: "Recusada", aguardando_complemento: "Aguardando complemento da Montadora" };

export function useTratativa(servicoId, s) {
  const [t, setT] = useState(null);
  const reload = () => api.get(`/servicos/${servicoId}/tratativa`).then(r => setT(r.data)).catch(() => setT(null));
  useEffect(() => { reload(); }, [servicoId, s?.status, s?.solicitacao_cancelamento?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  return t;
}

// Área "TRATATIVA DO IMPEDIMENTO" — visível para Empresa dona / Montadora vinculada / Admin
export function TratativaImpedimento({ servicoId, s, tratativa, onAcao, onSaved }) {
  if (!tratativa?.em_tratativa || tratativa.encaminhada_para) return null;
  const oc = tratativa.ocorrencia_atual;
  const classe = tratativa.classificacao;
  const acoes = (tratativa.acoes || []).filter(a => a !== "ocorrencia_gestao");
  const cor = classe === "produto" ? "border-[#E60000] bg-red-50" : "border-[#F5C518] bg-[#FFFBEA]";
  return (
    <div className={`border-2 ${cor} p-5 mb-6`} data-testid="tratativa-impedimento">
      <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
        <div>
          <div className="label-cap">Tratativa do impedimento</div>
          <div className="font-heading font-bold text-lg mt-1" data-testid="tratativa-motivo">
            {oc?.tipo_label || (s.status === "ausente" ? "Cliente ausente" : "Impedimento")}
          </div>
          {oc?.motivo && <div className="text-sm mt-1">{oc.motivo}</div>}
          <div className="text-[11px] text-muted-foreground mt-1">
            OS em <b className="capitalize">{s.status}</b> · classificação:{" "}
            <b data-testid="tratativa-classificacao">{classe === "produto" ? "PRODUTO" : classe === "logistica" ? "LOGÍSTICA" : "GERAL"}</b>
            {oc?.em && ` · ${new Date(oc.em).toLocaleString("pt-BR")} · ${oc.reportado_por || ""}`}
          </div>
        </div>
      </div>
      <div className="text-xs text-muted-foreground mb-3">
        {classe === "produto"
          ? "Ocorrência de PRODUTO: nova data não resolve. Encaminhe para Revisão ou Assistência Técnica (prazo legal de 30 dias) ou trate o cancelamento."
          : "Ocorrência de LOGÍSTICA: retome o atendimento com uma data alternativa do cliente, reagende formalmente, registre a falta ou trate o cancelamento."}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="tratativa-acoes">
        {acoes.filter(a => a !== "reaproveitar_data").map(a => (
          <Button key={a} variant="outline" className={`btn-sharp ${a === "cancelar" ? "border-[#E60000] text-[#E60000]" : ""}`}
            onClick={() => onAcao(a)} data-testid={`tratativa-acao-${a}`}>
            {ROTULOS[a] || a}
          </Button>
        ))}
      </div>
      {!tratativa.troca_disponivel && classe === "produto" && (
        <div className="text-[11px] text-muted-foreground mt-2" data-testid="tratativa-troca-indisponivel">
          Troca: {tratativa.troca_motivo}
        </div>
      )}
      {acoes.includes("reaproveitar_data") && (
        <div className="mt-4">
          <RetomadaDatas servicoId={servicoId} s={s} onSaved={onSaved} titulo="Reaproveitar data alternativa do cliente"
            semDatasTexto="Use o Reagendar formal acima." />
        </div>
      )}
    </div>
  );
}

// Painel da solicitação de cancelamento (Montadora vê status; Empresa/Admin decidem)
export function SolicitacaoCancelamento({ servicoId, tratativa, onSaved }) {
  const [decisao, setDecisao] = useState(null);
  const [obs, setObs] = useState("");
  const sol = tratativa?.solicitacao_cancelamento;
  if (!sol) return null;
  const decidir = async () => {
    try {
      const r = await api.post(`/servicos/${servicoId}/solicitacao-cancelamento/decidir`, { decisao, observacao: obs });
      toast.success(r.data.status === "aprovada" ? "Solicitação aprovada — OS cancelada"
        : r.data.status === "recusada" ? "Solicitação recusada" : "Complemento solicitado à Montadora");
      setDecisao(null); setObs(""); onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border border-border bg-white p-5 mb-6" data-testid="solicitacao-cancelamento">
      <div className="label-cap mb-1">Solicitação de cancelamento (Montadora)</div>
      <div className="text-sm"><b data-testid="solicitacao-status">{SOL_ROTULO[sol.status] || sol.status}</b></div>
      <div className="text-sm mt-1">Motivo: {sol.motivo}</div>
      {sol.complemento && <div className="text-sm mt-1">Complemento: {sol.complemento}</div>}
      {sol.decisao_observacao && <div className="text-sm mt-1">Observação da decisão: {sol.decisao_observacao}</div>}
      <div className="text-[11px] text-muted-foreground mt-1">
        {sol.solicitado_por} · {new Date(sol.em).toLocaleString("pt-BR")}
      </div>
      {tratativa.pode_decidir_solicitacao && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            {[["aprovar", "Aprovar e cancelar"], ["recusar", "Recusar"], ["complemento", "Solicitar complemento"]].map(([k, r]) => (
              <button key={k} onClick={() => setDecisao(k)}
                className={`btn-sharp px-4 py-2 text-sm ${decisao === k ? (k === "aprovar" ? "bg-[#E60000] text-white" : "brand-bg text-white") : "border border-border"}`}
                data-testid={`solicitacao-${k}`}>{r}</button>
            ))}
          </div>
          {decisao && (
            <>
              <div>
                <Label className="label-cap">{decisao === "aprovar" ? "Observação (opcional)" : "Justificativa / o que falta (obrigatório)"}</Label>
                <Textarea value={obs} onChange={e => setObs(e.target.value)} className="mt-2" data-testid="solicitacao-obs" />
              </div>
              <Button onClick={decidir} className="w-full btn-sharp brand-bg text-white" data-testid="solicitacao-confirmar">Confirmar decisão</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
